import { buildPlanCard, cssPlan, maybeLink, PlanContentModel } from "app/client/ui/PlanCard";
import { buttonSelect } from "app/client/ui2018/buttonSelect";
import { isNarrowScreenObs, mediaNotSmall, mediaSmall, theme } from "app/client/ui2018/cssVars";
import { TEAM_FREE_PLAN } from "app/common/Features";

import { Computed, dom, IDisposableOwner, Observable, styled } from "grainjs";

export interface PlansPageContent {
  header?: string;
  subHeader?: string;
  footer?: string;
  plans?: PlanContentModel[];
  currentPlan?: string | null; // current grist product (team, teamFree etc)
  currentPriceId?: string | null;
  reason?: "upgrade" | "renew" | "create"; // default is 'create'
}

export const CREATE_TEAM_HEADER = "Work as a Team";
export const UPGRADE_TEAM_HEADER = "Do more with your data";

export const SUB_HEADER_CREATE = `All paid plans include a 30-day free trial`;
export const SUB_HEADER_UPGRADE = "Upgrade for higher limits and more support";

export const FOOTER = "Show all features https://www.getgrist.com/pricing/";

export function buildPlansPage(
  owner: IDisposableOwner,
  options: PlansPageContent,
  onSelect: (plan: PlanContentModel) => Promise<any>,
) {
  if (!options.plans) {
    throw new Error("Plans are not provided");
  }

  // Make sure each plan has a priceId
  options.plans?.forEach((plan) => {
    if (!plan.priceId) {
      throw new Error(`Plan ${plan.gristProduct} does not have a priceId`);
    }
  });

  const isNarrowScreen = isNarrowScreenObs();
  const activePlan = Observable.create<PlanContentModel | null>(owner, options.plans.slice().pop()!);
  const activeMap = new Map<PlanContentModel, Observable<boolean>>();
  const isCurrent = (plan: PlanContentModel) => {
    if (options.currentPlan === TEAM_FREE_PLAN) {
      return plan.gristProduct === TEAM_FREE_PLAN;
    }
    if (options.currentPriceId && plan.priceId === options.currentPriceId) {
      return true;
    }
    return false;
  };
  function isCollapsed(plan: PlanContentModel) {
    if (!activeMap.has(plan)) {
      activeMap.set(plan, Computed.create(owner, (use) => {
        return (use(activePlan) !== plan || isCurrent(plan))  && use(isNarrowScreen);
      }));
    }
    return activeMap.get(plan);
  }
  function planClicked(plan: PlanContentModel) {
    // When current plan is clicked, don't expand it
    if (!isCurrent(plan)) {
      activePlan.set(plan);
    }
  }

  // Update the action show on the button (either downgrade/upgrade/change)
  const currentPlan = options.plans.find(isCurrent);
  options.plans.forEach((plan) => {
    plan.setUpgradeAction(currentPlan);
  });

  const defaultInterval = options.plans.find(isCurrent)?.interval || "month";
  const interval = Observable.create(owner, defaultInterval);
  const showIntervals = options.plans.some(plan => plan.interval === "year");

  return cssContainer(
    dom.maybe(options.header, () => cssHeaderLine(options.header)),
    dom.maybe(options.subHeader, subTitle => cssSubHeaderLine(subTitle)),
    showIntervals ? cssInterval(interval, [
      { value: "month", label: "Pay monthly" },
      { value: "year", label: "Pay yearly" },
    ]) : null,
    dom.maybe(options.subHeader || options?.header, () => hspace("10px")),
    cssPlansContainer(
      ...options.plans.map(plan => buildPlanCard({
        plan,
        interval,
        upgrade: options.reason === "upgrade",
        collapsed: isCollapsed(plan),
        isCurrent: isCurrent(plan),
        onSelect: async () => await onSelect(plan),
        onClick: () => planClicked(plan),
      })),
    ),
    dom.maybe(options.footer, footer => cssFooterContent(maybeLink(footer))),
  );
}

function hspace(height: string) {
  return dom("div", { style: `height: ${height}` });
}

const cssHeaderLine = styled("div", `
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
`);

const cssSubHeaderLine = styled("div", `
  text-align: center;
  margin-bottom: 7px;
`);

const cssPlansContainer = styled("div", `
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  @media (max-width: 1060px) {
    & {
      flex-direction: column;
      align-items: unset;
    }
    .${cssPlan.className} {
      min-height: 480px;
    }
  }
  @media ${mediaSmall} {
    .${cssPlan.className} {
      width: auto;
      min-height: unset;
    }
  }
`);

const cssFooterContent = styled("div", `
  display: grid;
  place-items: center;
  margin-top: 24px;
  @media ${mediaSmall} {
    & {
      font-size: 16px;
    }
  }
`);

const cssContainer = styled("div", `
  color: ${theme.text};
  @media ${mediaNotSmall} {
    &-wide {
      min-width: 700px;
    }
  }
`);

const cssInterval = styled(buttonSelect, `
  max-width: 200px;
  margin: 0 auto;
  margin-top: 16px;
  user-select: none;
`);
